import { capitalize } from 'lodash'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const CHARGE_STEP = { type: 'charge', title: 'Charge client' }
const PAYMENT_STEP = { type: 'payment', title: 'Select payment method' }
const GRATUITY_STEP = { type: 'gratuity', title: 'Add gratuity' }
const SEND_STEP = { type: 'send', title: undefined }

export function useChargeFlowStepManager(
    canEditTotal: boolean,
    allowGratuity?: boolean,
    recordTypeName?: string
) {
    const [searchParams, setSearchParams] = useSearchParams()

    const steps = useMemo(() => {
        let _steps = []

        if (canEditTotal) {
            _steps.push(CHARGE_STEP)
        }

        _steps.push(PAYMENT_STEP)

        if (allowGratuity) {
            _steps.push(GRATUITY_STEP)
        }

        _steps.push(SEND_STEP)

        return _steps.map((step, index) => ({ ...step, index }))
    }, [canEditTotal, allowGratuity])

    const currentStep = useMemo(() => {
        const stepFromParams = searchParams.get('step')

        return steps.find((step) => step.type === stepFromParams) ?? steps[0]
    }, [steps, searchParams])

    const backButtonTitle = useMemo(() => {
        if (currentStep.type === 'send') {
            return undefined
        }

        if (currentStep.index === 0) {
            return recordTypeName ?? 'Back'
        }
        return capitalize(steps[currentStep.index - 1].type)
    }, [steps, currentStep, recordTypeName])

    const goToNextStep = useCallback(
        (offset: number = 1) => {
            if (currentStep.index === steps.length - 1) {
                return
            }

            const nextStep = steps[currentStep.index + offset]
            setSearchParams((s) => {
                s.set('step', nextStep.type)
                return s
            })
        },
        [steps, currentStep, setSearchParams]
    )

    const goToPreviousStep = useCallback(() => {
        if (currentStep.index === 0) {
            return
        }

        const previousStep = steps[currentStep.index - 1]
        setSearchParams((s) => {
            s.set('step', previousStep.type)
            return s
        })
    }, [steps, currentStep, setSearchParams])

    return {
        steps,
        currentStep,
        goToNextStep,
        goToPreviousStep,
        backButtonTitle,
    }
}
